// @ts-nocheck
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  DollarSign, 
  RefreshCw, 
  AlertTriangle, 
  CheckCircle, 
  Scale,
  TrendingUp,
  TrendingDown,
  Clock
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { FinancialIntegrationService } from '@/services/financialIntegrationService';
import { useToast } from '@/hooks/use-toast';

interface FinancialCaseIntegrationProps {
  onRefresh?: () => void;
}

export const FinancialCaseIntegration: React.FC<FinancialCaseIntegrationProps> = ({ onRefresh }) => {
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState<{
    totalCases: number;
    casesWithFinancialData: number;
    casesWithoutFinancialData: number;
    totalMissingRevenue: number;
  } | null>(null);
  const { toast } = useToast();

  const analyzeFinancialIntegration = async () => {
    try {
      setLoading(true);
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Buscar todos os processos
      const { data: cases, error } = await supabase
        .from('cases')
        .select('id, title, case_number, client_fee, court_costs, case_value, archived')
        .eq('user_id', user.id)
        .eq('archived', false);

      if (error) throw error;

      // Buscar entradas financeiras existentes
      const { data: financialEntries, error: finError } = await supabase
        .from('financial_entries')
        .select('case_id')
        .eq('user_id', user.id);

      if (finError) throw finError;

      // Analisar dados
      const casesWithFinancialData = new Set(financialEntries?.map(entry => entry.case_id) || []);
      const casesWithoutFinancialData = cases?.filter(c => 
        !casesWithFinancialData.has(c.id) && 
        (parseFloat(c.client_fee || '0') > 0 || parseFloat(c.court_costs || '0') > 0)
      ) || [];

      const totalMissingRevenue = casesWithoutFinancialData.reduce((sum, c) => 
        sum + parseFloat(String(c.client_fee || '0')), 0
      );

      setStats({
        totalCases: cases?.length || 0,
        casesWithFinancialData: casesWithFinancialData.size,
        casesWithoutFinancialData: casesWithoutFinancialData.length,
        totalMissingRevenue
      });

    } catch (error) {
      console.error('Erro ao analisar integração financeira:', error);
      toast({
        title: "Erro",
        description: "Erro ao analisar integração financeira",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const syncAllCasesFinancialData = async () => {
    try {
      setLoading(true);
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Buscar todos os processos que têm dados financeiros mas não têm entradas
      const { data: cases, error } = await supabase
        .from('cases')
        .select('*')
        .eq('user_id', user.id)
        .eq('archived', false);

      if (error) throw error;

      let syncedCount = 0;
      let errorCount = 0;

      for (const caseData of cases || []) {
        try {
          // Verificar se já tem entradas financeiras
          const { data: existingEntries } = await supabase
            .from('financial_entries')
            .select('id')
            .eq('case_id', caseData.id)
            .limit(1);

          // Se não tem entradas e tem dados financeiros, criar
          if ((!existingEntries || existingEntries.length === 0) && 
              (parseFloat(String(caseData.client_fee || '0')) > 0 || parseFloat(String(caseData.court_costs || '0')) > 0)) {
            
            const financialData = {
              id: caseData.id,
              title: caseData.title,
              client_fee: String(caseData.client_fee || '0'),
              court_costs: String(caseData.court_costs || '0'),
              success_fee: String(caseData.success_fee || '0'),
              payment_method: 'a_vista' as const,
              installment_count: 1,
              installment_frequency: 'mensal' as const,
              first_payment_date: caseData.filing_date ? new Date(caseData.filing_date) : new Date(),
              late_payment_interest: '2',
              early_payment_discount: '0',
              payment_conditions: 'Pagamento conforme contrato',
              collection_notes: 'Processo sincronizado automaticamente',
              phase_fees: [],
              client_id: caseData.client_id,
              case_value: caseData.case_value?.toString() || '0',
              filing_date: caseData.filing_date ? new Date(caseData.filing_date) : new Date(),
              user_id: user.id
            };

            await FinancialIntegrationService.createFinancialEntriesFromProcess(financialData);
            syncedCount++;
          }
        } catch (error) {
          console.error(`Erro ao sincronizar processo ${caseData.id}:`, error);
          errorCount++;
        }
      }

      toast({
        title: "Sincronização Concluída",
        description: `${syncedCount} processo(s) sincronizado(s), ${errorCount} erro(s)`,
      });

      // Atualizar estatísticas
      await analyzeFinancialIntegration();
      onRefresh?.();

    } catch (error) {
      console.error('Erro na sincronização:', error);
      toast({
        title: "Erro",
        description: "Erro durante a sincronização",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    analyzeFinancialIntegration();
  }, []);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  return (
    <div className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white">
      <div className="flex flex-wrap items-end justify-between gap-3 border-b border-slate-100 px-5 py-4 sm:px-6">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#9a7f52]">Integração</p>
          <p className="mt-1 text-lg font-semibold tracking-tight text-slate-900">Processos ligados ao Financeiro</p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={analyzeFinancialIntegration}
            disabled={loading}
            variant="ghost"
            size="sm"
            className="h-9 rounded-xl text-[13px] text-slate-500 hover:text-slate-900"
          >
            <RefreshCw className={`mr-1.5 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Verificar
          </Button>
          {stats && stats.casesWithoutFinancialData > 0 && (
            <Button
              onClick={syncAllCasesFinancialData}
              disabled={loading}
              size="sm"
              className="h-9 rounded-xl bg-slate-900 text-[13px] text-white hover:bg-slate-800"
            >
              <DollarSign className="mr-1.5 h-4 w-4 text-[#e8d5a8]" />
              Sincronizar {stats.casesWithoutFinancialData}
            </Button>
          )}
        </div>
      </div>

      {stats && (
        <>
          <div className="grid grid-cols-2 gap-px bg-slate-100 sm:grid-cols-4">
            {[
              { label: 'Processos ativos', value: String(stats.totalCases), className: 'text-slate-900' },
              { label: 'Com lançamentos', value: String(stats.casesWithFinancialData), className: 'text-slate-900' },
              { label: 'Sem integração', value: String(stats.casesWithoutFinancialData), className: stats.casesWithoutFinancialData ? 'text-red-600' : 'text-slate-300' },
              { label: 'Honorários fora do Financeiro', value: formatCurrency(stats.totalMissingRevenue), className: stats.totalMissingRevenue ? 'text-amber-600' : 'text-slate-300' },
            ].map((tile) => (
              <div key={tile.label} className="bg-white px-5 py-4">
                <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">{tile.label}</p>
                <p className={`mt-1 text-2xl font-semibold tabular-nums tracking-tight ${tile.className}`}>{tile.value}</p>
              </div>
            ))}
          </div>
          <div className="border-t border-slate-100 px-5 py-3 text-sm sm:px-6">
            {stats.casesWithoutFinancialData > 0 ? (
              <p className="flex items-center gap-2 text-amber-700">
                <AlertTriangle className="h-4 w-4 shrink-0" />
                {stats.casesWithoutFinancialData} {stats.casesWithoutFinancialData === 1 ? 'processo tem' : 'processos têm'} honorários cadastrados que ainda não viraram lançamentos. Use “Sincronizar”.
              </p>
            ) : (
              <p className="flex items-center gap-2 text-emerald-700">
                <CheckCircle className="h-4 w-4 shrink-0" />
                Todos os processos com honorários já estão no Financeiro.
              </p>
            )}
          </div>
        </>
      )}
    </div>
  );
};
